import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';

export interface RelayAgent {
  id: string;
  name: string;
  status: string;
  site_id: string | null;
  version: string | null;
  capabilities: string[];
  ip_address: string | null;
  last_heartbeat: string | null;
  is_enabled: boolean;
  created_at: string;
}

export interface CreateRelayTaskInput {
  type: string;
  agent_id?: string;
  payload: Record<string, unknown>;
  priority?: string;
  timeout?: number;
}

export interface RelayTaskResponse {
  task_id: string;
  status: string;
  type: string;
}

export interface QueueTask {
  id: string;
  type: string;
  status: string;
  priority: string;
  payload: Record<string, unknown>;
  error: string | null;
  agent_id: string | null;
  agent_name: string | null;
  created_at: string;
  assigned_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  can_cancel: boolean;
}

export interface QueueSummary {
  pending: number;
  assigned: number;
  completed: number;
  failed: number;
  cancelled: number;
}

export interface QueueResponse {
  tasks: QueueTask[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  summary: QueueSummary;
}

export interface QueueFilters {
  status?: string;
  type?: string;
  agent_id?: string;
  page?: number;
  limit?: number;
}

/**
 * Hook to fetch available relay agents
 */
export function useRelayAgents() {
  return useQuery({
    queryKey: ['relay', 'agents'],
    queryFn: async () => {
      const response = await api.get<{ agents: RelayAgent[] }>(endpoints.relay.agents);
      return response.agents;
    },
  });
}

/**
 * Hook to create a new relay task
 */
export function useCreateRelayTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRelayTaskInput) => {
      return api.post<RelayTaskResponse>(endpoints.relay.createTask, input);
    },
    onSuccess: () => {
      // Invalidate tasks list to show the new task
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: ['relay', 'queue'] });
    },
  });
}

/**
 * Hook to fetch the task queue with filters
 */
export function useTaskQueue(filters: QueueFilters = {}) {
  return useQuery({
    queryKey: ['relay', 'queue', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.agent_id) params.agent_id = filters.agent_id;
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;

      return api.get<QueueResponse>(endpoints.relay.queue, { params });
    },
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });
}

/**
 * Hook to cancel a relay task
 */
export function useCancelRelayTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      return api.post<{ success: boolean; message: string }>(endpoints.relay.cancelTask(taskId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['relay', 'queue'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}
